import { LogEntry, LogEntryType } from '@/parsing/LogEntry';
import { LogLine } from '@/parsing/LogLine';

export enum MQTTMessageLogEvent {
  Generic,
  ConnectionStatus,
  ReceivedMessage,
  PublishedMessage,
}

export interface MQTTMessageLogEntryExtra {
  event: MQTTMessageLogEvent;
  url: string | null;
  command: string;
  params: { [key: string]: any };
}

type Params = Record<string, any>;

function parseParameters(command: string): Params {
  const regex = /(\w+)=(".*?"|\{.*\}|[a-zA-Z0-9._\-/"?=: ]+)/g;
  const params: Params = {};

  let match: RegExpExecArray | null;
  do {
    match = regex.exec(command);
    if (match) {
      const [_, key, value] = match;
      params[key] = value;
    }
  } while (match !== null);

  return params;
}

function parseBoltMQTTMessage(entry: LogEntry): MQTTMessageLogEntryExtra {
  const regex = /([a-zA-Z0-9._-]+:[a-zA-Z0-9._-]+)\s*-\s*([a-zA-Z_][a-zA-Z0-9_]+)\s*(.*)/;
  const match = entry.content.match(regex);

  if (!match) {
    throw new Error(`Cannot parse Bolt MQTT message: '${entry.content}'`);
  }

  const [, url, commandName, paramsPart] = match;
  if (!url || !commandName) {
    throw new Error(`Invalid Bolt MQTT message format: '${entry.content}'`);
  }

  return {
    event: getEventForCommand(commandName),
    url,
    command: commandName,
    params: parseParameters(paramsPart),
  };
}

function parseCocoaMQTTMessage(entry: LogEntry): MQTTMessageLogEntryExtra {
  const splitToken = 'CocoaMQTTLogger';
  const parts = entry.content.split(splitToken);

  if (parts.length < 2) {
    throw new Error(`Cannot parse Cocoa MQTT message: '${entry.content}'`);
  }

  const paramsPart = parts[1].trim();

  return {
    event: MQTTMessageLogEvent.Generic,
    url: null,
    command: 'CocoaMQTTLogger',
    params: { params: paramsPart },
  };
}

function getEventForCommand(command: string): MQTTMessageLogEvent {
  switch (command) {
    case 'ConnectionStatusDidChange':
      return MQTTMessageLogEvent.ConnectionStatus;
    case 'Publishing':
      return MQTTMessageLogEvent.PublishedMessage;
    case 'ReceivedMessage':
      return MQTTMessageLogEvent.ReceivedMessage;
    default:
      return MQTTMessageLogEvent.Generic;
  }
}

export function parseMQTTMessage(_line: LogLine, entry: LogEntry): void {
  const parsers = [parseCocoaMQTTMessage, parseBoltMQTTMessage];

  for (const parser of parsers) {
    try {
      const parsedMessage = parser(entry);
      entry.extra.mqttMessage = parsedMessage;
      // Only classify as MQTT once we actually have a parsed message,
      // otherwise the entry is routed to the MQTT cell with no extra.mqttMessage.
      entry.entryType = LogEntryType.MQTTMessage;
      return;
    } catch {
      // Continue trying other parsers
    }
  }

  throw new Error(`Unable to parse MQTT message: '${entry.content}'`);
}
